import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { LoadingButton } from '@mui/lab'

import ErrorIcon from '@mui/icons-material/Error'
import { Box, Card, CardContent, CircularProgress, Divider, Grid, IconButton, Skeleton, Stack, Typography, Collapse } from '@mui/material'
import { UseFormReturn, useWatch } from 'react-hook-form'
import agent from '../../agent'
import { StringsContext, useStrings } from '../../assets/localization/strings'
import { Country, Service, UnboxingType, VideoDuration, VideoFeature, VideoType } from '../../models/GeneralTypes'
import { delay, numberToEuro, State, useCancellablePromise } from '../../reusableUtils/Helpers'
import rootStore from '../../stores/rootStore'
import { CouponCodeView } from './CouponView'
import type { IUGCCampaign } from './UGCCampaignForm'
import { ICompanyForm } from '../../types/paymentModal'
import { getCurrencySymbolByCountry } from '../../reusableUtils/currency'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useSnackbar } from 'notistack'

export type Invoice = Awaited<ReturnType<typeof agent.Brands.previewInvoice>>

type Data = {
  numberOfVideos?: number
  videoType?: VideoType
  unboxingType?: UnboxingType
  videoFeature?: VideoFeature
  videoDuration?: VideoDuration
  product: Service | undefined
  brand?: object
  numberOfPhotos?: number
  hooks?: string[]
  ctas?: string[]
}
type PricingContext = State<'invoice', Invoice | Error | undefined> & {
  ignoreLoad: React.MutableRefObject<boolean>
  invoiceToUse: React.MutableRefObject<Invoice | undefined>
  initialCouponRef: React.MutableRefObject<string | undefined>
  data: Data
  load: (delayDuration?: number, data?: ICompanyForm) => Promise<void>
} & State<'couponCode', string>

export const usePricingForm = (data: Data): PricingContext => {
  const [invoice, setInvoice] = useState<Invoice | Error>()
  const [ignoreLoad, invoiceToUse] = [useRef(false), useRef<Invoice>()]
  const initialCouponRef = useRef(Object.fromEntries(new URLSearchParams(window.location.search).entries()).couponCode as string | undefined)
  const [couponCode, setCouponCode] = useState('')
  const { product, numberOfVideos, videoDuration, videoFeature, videoType, unboxingType, numberOfPhotos, brand, hooks = [], ctas = [] } = data

  const launch = useCancellablePromise()
  const load = (delayDuration = 1000, data?: ICompanyForm) =>
    launch(async ensureIsActive => {
      setInvoice(undefined)
      await delay(delayDuration)
      ensureIsActive()
      const paymentData = data || brand
      try {
        const result = await rootStore.brandStore.previewInvoice(
          numberOfVideos ?? 1,
          videoType,
          videoFeature,
          videoDuration,
          initialCouponRef.current || couponCode,
          product,
          paymentData,
          hooks,
          ctas,
          numberOfPhotos ?? 0
        )
        ensureIsActive()
        invoiceToUse.current = result
        setInvoice(result)
        if (initialCouponRef.current) setCouponCode(initialCouponRef.current)
      } catch (e) {
        ensureIsActive()
        setInvoice(e)
        if (!invoiceToUse.current && e['status'] == 422) setCouponCode('')
        if (initialCouponRef.current) load(0)
      }
      initialCouponRef.current = undefined
    })

  useEffect(() => {
    if (ignoreLoad?.current && invoiceToUse.current) {
      ignoreLoad.current = false
      return
    }
    load()
  }, [
    JSON.stringify(brand),
    numberOfVideos,
    videoDuration,
    videoFeature,
    videoType,
    unboxingType,
    couponCode,
    product,
    JSON.stringify(hooks),
    JSON.stringify(ctas),
    numberOfPhotos
  ])

  return { invoice, setInvoice, ignoreLoad, invoiceToUse, initialCouponRef, data, couponCode, setCouponCode, load }
}
export const usePricingFormByContext = (control: UseFormReturn<IUGCCampaign>['control'], brand?: object): PricingContext => {
  const numberOfVideos = +useWatch({ control, name: 'numberOfVideos' }) || 0 // sometimes it turns to string when empty
  const videoDuration = useWatch({ control, name: 'videoDuration' }) as VideoDuration
  const videoFeature = useWatch({ control, name: 'videoFeature' })
  const videoType = useWatch({ control, name: 'videoType' })
  const unboxingType = useWatch({ control, name: 'unboxingType' }) as UnboxingType
  const service = useWatch({ control, name: 'service' })
  const numberOfHooks = useWatch({ control, name: 'numberOfHooks' })
  const numberOfCtas = useWatch({ control, name: 'numberOfCtas' })
  const numberOfPhotos = +useWatch({ control, name: 'numberOfPhotos' })
  return usePricingForm({
    numberOfVideos,
    numberOfPhotos,
    videoDuration,
    videoFeature,
    videoType,
    unboxingType,
    product: service,
    brand,
    hooks: Array.from({ length: numberOfHooks }, (_, index) => (index + 1).toString()),
    ctas: Array.from({ length: numberOfCtas }, (_, index) => (index + 1).toString())
  })
}

export const PricingFormContext = createContext(undefined as unknown as PricingContext)

interface Props {
  mobile?: boolean
  title?: string
  body?: string
  mt?: number
  formValues?: ICompanyForm
  isInvoiceLoading?: boolean
  onChangeLoadingInvoice?: (value: boolean) => void
  checkout?: boolean
  createCampaign?: () => Promise<{
    brandId: string;
    campaignId: string;
  }>
  noPayment?: boolean
}

export const PricingBasedOnInvoice = (props: Props) => {
  const strings = useStrings()
  const {
    invoiceToUse,
    invoice,
    setInvoice,
    data: { videoType, videoDuration, videoFeature, numberOfVideos, hooks, ctas, numberOfPhotos, product, brand },
    couponCode,
    setCouponCode: setCoupon,
    load
  } = useContext(PricingFormContext)

  const { language } = useContext(StringsContext)
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const { title = strings.preview_invoice_title, body = strings.preview_invoice_body, mt, mobile = false, formValues, isInvoiceLoading, checkout, noPayment, onChangeLoadingInvoice, createCampaign } = props
  const initialCouponRef = useRef(Object.fromEntries(new URLSearchParams(window.location.search).entries()).couponCode as string | undefined)
  const couponFocused = useRef(false)
  const invoiceCountry = rootStore.brandStore.selectedBrand?.country?.toLowerCase() || rootStore.brandStore.selectedBrand?.brandCountry
  const currencySymbol = invoiceCountry ? getCurrencySymbolByCountry(invoiceCountry) : '€'

  const vatTxt = invoiceCountry === Country.great_britain ? strings.preview_invoice_ust_20 : strings.preview_invoice_ust_19

  // --- Collapsible State ---
  const [expanded, setExpanded] = useState(false)
  const [isManual, setIsManual] = useState(false)
  const toggleExpanded = () => { setExpanded(prev => !prev); setIsManual(true) }
  // -------------------------

  useEffect(() => {
    if (formValues) {
      load(1000, formValues)
    }
  }, [formValues?.vatNumber, formValues?.vatNumberCheck, formValues?.country, formValues?.zip])

  useEffect(() => {
    if (!invoice && !isInvoiceLoading) {
      onChangeLoadingInvoice && onChangeLoadingInvoice(true)
    }
    if (invoice && isInvoiceLoading) {
      onChangeLoadingInvoice && onChangeLoadingInvoice(false)
    }
  }, [invoice, isInvoiceLoading])

  useEffect(() => {
    if (checkout) setExpanded(checkout)
  }, [checkout])

  useEffect(() => {
    const currentCountry = rootStore.brandStore.selectedBrand?.country?.toLocaleLowerCase()
    if ((currentCountry === Country.great_britain || language === 'en') && !expanded && !isManual) setExpanded(true)
  }, [rootStore.brandStore.selectedBrand.country, expanded, language, isManual])

  const renderLine = ({ key, label, value, rawValue }: { key?: string; label: string; value: string; rawValue?: number }) => {
    // calculate quantity for each item
    let qty: number = null
    switch (key) {
      case 'ad':
      case 'premuim':
      case 'unboxing':
      case 'small':
      case 'medium':
        qty = numberOfVideos
        break
      case 'photos':
        qty = numberOfPhotos
        break
      case 'hook':
        qty = hooks.length - 1
        break
      case 'cta':
        qty = ctas.length - 1
        break
      default:
        break
    }
    const color = value.startsWith('- ') ? 'orange' : '#190851' // discount => orange
    const mb = mobile ? 0.6 : '12px'
    return (
      <React.Fragment key={key}>
        <Grid item xs={8} mb={mb} justifyContent={'center'}>
          <Typography color={color} sx={{ fontSize: '16px', fontWeight: 500, textTransform: 'capitalize' }}>
            {label}
          </Typography>
          {qty && (
            <Typography color={color} sx={{ fontSize: '14px', fontWeight: 300, textTransform: 'capitalize' }}>
              {strings.quantity} {qty}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} mb={mb} textAlign='right'>
          <Typography color={color} sx={{ fontSize: '16px', fontWeight: 500 }}>
            {value}
          </Typography>
          {qty && rawValue > 0 && (
            <Typography color={color} sx={{ fontSize: '14px', fontWeight: 300 }}>
              {`${currencySymbol} ${numberToEuro(rawValue / qty)} ${strings.each}`}
            </Typography>
          )}
        </Grid>
      </React.Fragment>
    )
  }

  const renderCoupon = (type: 'percent' | 'amount', value: number | null) => {
    if (value == null || value === 0) return null
    return renderLine({
      label: strings.preview_invoice_discount,
      value: type === 'amount' ? `- ${currencySymbol} ${numberToEuro(value)}` : `- ${value}%`
    })
  }

  const renderDivider = () => (
    <Grid item xs={12} mb={1}>
      <Divider />
    </Grid>
  )

  const labels = {
    [VideoFeature.small]: strings.pricing_video_feature_small,
    [VideoFeature.medium]: strings.pricing_video_feature_medium,
    [VideoFeature.up_small_medium]: strings.pricing_video_feature_medium,
    [VideoDuration.Sec15]: strings.pricing_video_duration_s,
    [VideoDuration.Sec30]: strings.pricing_video_duration_m,
    [VideoDuration.Sec60]: strings.pricing_video_duration_l,
    [VideoType.ad]: strings.pricing_video_type_ad,
    [VideoType.premium]: strings.pricing_video_type_premium,
    [VideoType.unboxing]: strings.pricing_video_type_unboxing,
    [Service.selfService]: strings.pricing_self_service,
    [Service.nanoService]: strings.pricing_nano_service,
    photos: strings.pricing_photo,
    ['hook']: strings.pricing_hooks(1),
    ['cta']: strings.pricing_ctas(1)
  }

  const inv = invoiceToUse.current
  const shouldRenderTax = inv?.tax_exempt !== 'exempt'

  // Helper to compute the top-level "Total payable amount"
  const topAmount = inv?.amount_due != null ? inv.amount_due : inv?.total != null ? inv.total : 0

  const handleCheckout = async () => {
    try {
      const cancelUrl = window.location.href;
      const successUrl = `${window.location.origin}/`;
      setIsLoading(true)
      const campaign = await createCampaign()
      if (noPayment && campaign) {
        window.location.href = successUrl;
      }

      if (!noPayment && campaign) {
        try {
          const session = await rootStore.brandStore.checkout(
            {
              numberOfVideos: numberOfVideos ?? 1,
              videoType,
              videoFeature,
              videoDuration,
              coupon: initialCouponRef.current || couponCode,
              service: product,
              paymentData: brand,
              hooks,
              ctas,
              cancelUrl,
              successUrl,
              numberOfPhotos: numberOfPhotos ?? 0,
              campaignId: campaign.campaignId
            }
          )
          setIsLoading(false)
          const redirectUrl = session?.url || successUrl;
          window.location.href = redirectUrl;
        } catch (e) {
          enqueueSnackbar(e.message, { variant: 'error' })
        }
      }
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
  }
  return (
    <>
      {/* --- Collapsible Trigger Row --- */}
      {!checkout && (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ cursor: 'pointer', mb: 1, borderRadius: '10px', backgroundColor: '#AB95FF', py: '20px', px: '24px', position: 'sticky' }}
          onClick={!checkout && toggleExpanded}>
          <Typography fontSize={'16px'} fontWeight={500}>
            {strings.campaign_creation_receipt_title}
          </Typography>
          <div style={{ display: 'flex' }}>
            <Typography fontSize={'16px'} fontWeight={500}>
              {inv ? `${currencySymbol}${numberToEuro(topAmount)}` : ''}
            </Typography>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        </Stack>
      )}
      {/* --- Collapsible Content --- */}
      <Collapse in={expanded}>
        {inv ? (
          <Stack mt={mt}>
            <Box className='zstack'>
              <Card variant='outlined' sx={{ p: mobile ? 0 : undefined, backgroundColor: checkout ? 'transparent' : '#AB95FF1A', mb: '24px' }}>
                <CardContent sx={{ p: mobile ? 1 : undefined }}>
                  <Grid container>
                    {!mobile && (
                      <>
                        <Grid item xs={12} mb={2}>
                          <Typography sx={{ fontFamily: 'Bricolage Grotesque', fontSize: '24px', fontWeight: 500, color: '#190851' }}>{title}</Typography>
                        </Grid>
                        <Grid item xs={12} mb={2}>
                          <Typography sx={{ fontSize: '30px', fontWeight: 600, color: '#190851' }}>{inv ? `${currencySymbol}${numberToEuro(topAmount)}` : ''}</Typography>
                        </Grid>
                      </>
                    )}

                    {inv.items.map(item =>
                      renderLine({
                        key: `${item.name}`,
                        label: `${labels[item.name]}`,
                        value: `${currencySymbol} ${numberToEuro(item.value)}`,
                        rawValue: item.value
                      })
                    )}
                    {renderDivider()}
                    {shouldRenderTax &&
                      renderLine({
                        label: strings.preview_invoice_netto,
                        value: `${currencySymbol} ${numberToEuro(inv.subtotal ?? 0)}`
                      })}
                    {renderDivider()}
                    {renderCoupon('amount', inv.amount_off)}
                    {renderCoupon('percent', inv.percent_off)}
                    {shouldRenderTax &&
                      (inv.amount_off > 0 || inv.percent_off > 0) &&
                      renderLine({
                        // label: strings.preview_invoice_netto,
                        label: 'Total excluding tax',
                        value: `${currencySymbol} ${numberToEuro(inv.beforeTax ?? 0)}`
                      })}
                    {shouldRenderTax &&
                      renderLine({
                        label: inv.tax_exempt === 'reverse' ? strings.preview_invoice_reverse_charge : vatTxt,
                        value: `${currencySymbol} ${numberToEuro(inv.tax ?? 0)}`
                      })}
                    {renderDivider()}
                    {shouldRenderTax &&
                      renderLine({
                        label: strings.preview_invoice_brutto,
                        value: `${currencySymbol} ${numberToEuro(inv.total)}`
                      })}
                    {!!inv.appliedBalance && renderDivider()}
                    {!!inv.appliedBalance &&
                      !!inv.total &&
                      renderLine({
                        label: 'Guthaben',
                        value: `- ${currencySymbol} ${numberToEuro(inv.appliedBalance)}`
                      })}
                    {!!inv.appliedBalance &&
                      renderLine({
                        label: 'Zu zahlen:',
                        value: `${currencySymbol} ${numberToEuro(inv.amount_due)}`
                      })}
                  </Grid>
                  <CouponCodeView
                    coupon={couponCode}
                    setCoupon={setCoupon}
                    state={invoice == null || invoice instanceof Error ? invoice : {}}
                    load={load}
                    couponFocused={couponFocused}
                  />
                  {checkout && (
                    <LoadingButton
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '16px',
                        backgroundColor: '#a890fe',
                        borderRadius: '10px',
                        textAlign: 'center',
                        color: '#ffffff',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 500,
                        py: '16px',
                        px: '20px'
                      }}
                      type='button'
                      loading={isLoading}
                      id={'CREATE_CAMPAIGN_FORM'} onClick={handleCheckout}>
                      <p>{strings.campaign_creation_create}</p>
                      <p>{inv ? `${currencySymbol}${numberToEuro(topAmount)}` : ''}</p>
                    </LoadingButton>
                  )}
                </CardContent>
              </Card>

              {/* Loading / Error Overlays */}
              {((invoice == null && (!couponFocused.current || !couponCode)) || (invoice instanceof Error && (invoice as any)['status'] !== 422)) && (
                <>
                  {invoiceToUse.current && <Box sx={{ backgroundColor: 'gray', opacity: 0.2 }} width='100%' height='100%' />}
                  {invoice == null ? (
                    <CircularProgress />
                  ) : (
                    <IconButton onClick={() => load(0)}>
                      <ErrorIcon />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          </Stack>
        ) : (
          <Skeleton height='400px' sx={{ width: '400px', maxWidth: '100%', mt }} />
        )}
      </Collapse>
    </>
  )
}

export const PricingInUGCForm = PricingBasedOnInvoice
